import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateConstantsModificats } from "../../actions/configFormularioAction";
import { useTranslation } from "react-i18next";
import { crearErrorAction } from "../../actions/ui";

const IconSave = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V7l-4-4zm-5 16a3 3 0 110-6 3 3 0 010 6zm3-10H5V5h10v4z"/>
  </svg>
);

// ── Helpers coma ↔ punto ──────────────────────────────────────────────────────
// BD/PHP usa punto: "40.2"  →  usuario ve coma: "40,2"
const puntoAComa  = (val) => val !== undefined && val !== null ? String(val).replace('.', ',') : '';
const comaAPunto  = (val) => String(val).replace(',', '.');
// Compara ignorando la diferencia coma/punto
const mismoValor  = (a, b) => comaAPunto(String(a ?? '')) === comaAPunto(String(b ?? ''));

export const ComponenteFormularioConstantes = ({ catIndex }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation("generic");
  const { auth, configFormulario, ui } = useSelector((state) => state);
  const { infoFormularioTextCategoria, infoFormularioConstantesCategoria } = configFormulario;
  const { msgError } = ui;

  const [editableQuestions, setEditableQuestions] = useState([]);

  useEffect(() => {
    if (!infoFormularioTextCategoria || !infoFormularioConstantesCategoria) return;

    const categoryKey              = `Categoria${catIndex}`;
    const categoryTextData         = infoFormularioTextCategoria[categoryKey] || {};
    const categoryConstantesActual = infoFormularioConstantesCategoria.actualVersion?.[categoryKey] || {};
    const categoryConstantesNueva  = infoFormularioConstantesCategoria.nuevaVersion?.[categoryKey] || {};

    const formattedQuestions = Object.keys(categoryTextData)
      .filter((key) => key.includes("Preg"))
      .map((key) => {
        const match          = key.match(/Preg(\d+)/);
        const questionNumber = match ? match[1] : "X";
        const constantKey    = `${catIndex}.${questionNumber}`;
        const actualValue    = categoryConstantesActual[constantKey];
        const newValue       = categoryConstantesNueva[constantKey];
        const hasConstant    = actualValue !== undefined;

        return {
          id:          key,
          text:        categoryTextData[key] || "Pregunta sin texto",
          actualValue: hasConstant ? puntoAComa(actualValue) : "—",   // muestra con coma
          newValue:    hasConstant ? puntoAComa(newValue)    : "No existe constante para esta pregunta",
          editable:    hasConstant,
        };
      });

    setEditableQuestions(formattedQuestions);
  }, [infoFormularioTextCategoria, infoFormularioConstantesCategoria, catIndex]);

  const handleQuestionChange = (e, id) => {
    // Permitir escribir libremente (con coma o punto), se normaliza al guardar
    const raw = e.target.value;
    // Solo permitir dígitos, coma y punto
    if (!/^[\d,.\-]*$/.test(raw)) return;
    setEditableQuestions(editableQuestions.map((q) =>
      q.id === id ? { ...q, newValue: raw } : q
    ));
  };

  const handleBlur = (id) => {
    // Al salir del campo, normalizar a coma para consistencia visual
    setEditableQuestions(editableQuestions.map((q) => {
      if (q.id !== id) return q;
      // Sustituir punto por coma para mostrar
      const normalizado = q.newValue.replace('.', ',');
      return { ...q, newValue: normalizado };
    }));
  };

  const handleModificarTextos = () => {
    const dataToSend = {};
    const currentCategory = infoFormularioConstantesCategoria.nuevaVersion?.[`Categoria${catIndex}`];

    editableQuestions.forEach((question) => {
      if (!question.editable) return;
      const formattedKey  = question.id.replace(/^Cat(\d+)_Preg(\d+)$/, '$1.$2');
      const valorOriginal = currentCategory?.[formattedKey];

      // Comparar con punto (formato BD)
      if (!mismoValor(question.newValue, valorOriginal)) {
        dataToSend[formattedKey] = comaAPunto(question.newValue); // enviar con punto al PHP
      }
    });

    if (Object.keys(dataToSend).length > 0) {
      dispatch(updateConstantsModificats(
        auth.tokenJwt, dataToSend,
        `Categoria${catIndex}`,
        infoFormularioConstantesCategoria.nuevaVersion.version
      ));
    } else {
      dispatch(crearErrorAction("No se hicieron cambios."));
    }
  };

  return (
    <div style={{ padding:'20px 24px' }}>
      {msgError && (
        <div style={{ padding:'10px 14px', borderRadius:9, background:'#fef2f2', border:'1px solid #fecaca', color:'#dc2626', fontSize:13, fontWeight:500, marginBottom:14 }}>
          {msgError}
        </div>
      )}

      <div style={{ borderRadius:10, border:'1px solid #e5e7eb', overflow:'hidden', marginBottom:16 }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:14 }}>
          <thead>
            <tr style={{ background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>
              <th style={{ padding:'10px 14px', textAlign:'left', fontSize:12, fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>Pregunta</th>
              <th style={{ padding:'10px 14px', textAlign:'center', fontSize:12, fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em', width:120 }}>Valor actual</th>
              <th style={{ padding:'10px 14px', textAlign:'center', fontSize:12, fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em', width:150 }}>Nuevo valor</th>
            </tr>
          </thead>
          <tbody>
            {editableQuestions.map((question, index) => {
              const currentCategory = infoFormularioConstantesCategoria.nuevaVersion?.[`Categoria${catIndex}`];
              const formattedKey    = question.id.replace(/^Cat(\d+)_Preg(\d+)$/, '$1.$2');
              const changed         = question.editable && !mismoValor(question.newValue, currentCategory?.[formattedKey]);
              return (
                <tr key={index} style={{ borderBottom:'1px solid #f3f4f6', background: changed ? '#fffbeb' : '#fff' }}>
                  <td style={{ padding:'10px 14px', color:'#374151' }}>{question.text}</td>
                  <td style={{ padding:'10px 14px', textAlign:'center', color:'#9ca3af', fontWeight:500 }}>
                    {question.actualValue}
                  </td>
                  <td style={{ padding:'10px 14px', textAlign:'center' }}>
                    {question.editable ? (
                      <input
                        type="text"
                        inputMode="decimal"
                        value={question.newValue}
                        onChange={(e) => handleQuestionChange(e, question.id)}
                        onBlur={() => handleBlur(question.id)}
                        style={{ width:'100%', padding:'6px 10px', fontSize:14, border:`1.5px solid ${changed?'#f59e0b':'#e5e7eb'}`, borderRadius:7, outline:'none', color:'#111827', background:'#fff', boxSizing:'border-box' }}
                      />
                    ) : (
                      <span style={{ fontSize:12, color:'#9ca3af', fontStyle:'italic' }}>Sin constante</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button onClick={handleModificarTextos} className="u-btn-primary" style={{ display:'inline-flex', alignItems:'center', gap:7 }}>
        <IconSave /> Guardar nuevos valores
      </button>
    </div>
  );
};

